import React, { useEffect, useState, useRef } from "react";
import { Formik } from "formik";
import { inject, observer } from "mobx-react";
import { message, Form, Row, Col, Typography, Card, Divider, Empty, Tooltip } from "antd";
import * as Yup from "yup";
import FormCheckBoxInput from "components/FormItems/FormCheckBoxInput";
import FormInput from "components/FormItems/FormInput";
import FormSelectInput from "components/FormItems/FormSelectInput";
import AppButton from "components/AppButton/AppButton";
import moment from "moment";
import { dateTimeFormat } from "constant/constant";

function PurchaseOrder(props) {
  const { onClose, user, userStore } = props;
  const [currencyList, setCurrencyList] = useState();
  const [orders, setOrders] = useState();
  const [creditInfo, setCreditInfo] = useState();
  const [editableOrder, setEditableOrder] = useState();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const formikRef = useRef();
  const { addPurchaseOrder, fetchCurrencies, getUserAllPurchaseOrders, deletePurchaseOrder, updatePurchaseOrder } = userStore;

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchCurrencyList();
      fetchOrders();
    }
  }, [user]);

  function fetchCurrencyList() {
    fetchCurrencies().then(({ data }) => {
      if (data) setCurrencyList(data);
    });
  }

  function fetchOrders() {
    getUserAllPurchaseOrders({ user: user?._id })
      .then(data => {
        setCreditInfo(data.balanceObj);
        if (data.orders) {
          setOrders(data.orders);
        }
      })
      .finally(() => setLoading(false));
  }

  function deleteOrder({ orderId }) {
    return deletePurchaseOrder({ orderId, userToSearch: user?._id }).then(data => {
      fetchOrders();
    });
  }

  return (
    <Row gutter={[20, 20]} justify="center" type="flex">
      <Col span={12}>
        <Card className="rounded" hoverable loading={loading} style={{ minHeight: 116 }}>
          <div className="d-flex flex-column">
            <Typography.Text strong className="fs-6">
              Name: {user && user.name}
            </Typography.Text>
            {user && user.email && (
              <Typography.Paragraph strong className="mb-0" copyable>
                Email: {user.email}
              </Typography.Paragraph>
            )}
            {user && user.mobile && (
              <Typography.Paragraph strong className="mb-0" copyable>
                Mobile: {user.mobile}
              </Typography.Paragraph>
            )}
          </div>
        </Card>
      </Col>
      <Col span={12}>
        <Card className="rounded" hoverable loading={loading} style={{ minHeight: 116 }}>
          <div className="d-flex flex-column">
            <Typography.Text strong className="fs-6">
              Balance: {creditInfo && creditInfo.symbol[0]} {creditInfo && creditInfo.balance}
            </Typography.Text>
            <Typography.Text strong>SMS: {creditInfo && creditInfo.maxPossibleCredits.sms} Credits</Typography.Text>
            <Typography.Text strong>MMS: {creditInfo && creditInfo.maxPossibleCredits.mms} Credits</Typography.Text>
          </div>
        </Card>
      </Col>
      <Col span={24}>
        <Card className="rounded">
          <Formik
            enableReinitialize={true}
            initialValues={
              !editableOrder
                ? {
                    user: user && user._id,
                    totalAmount: null,
                    currency: "",
                    isSMSAllow: true,
                    isMMSAllow: true,
                    centsPerSMS: null,
                    centsPerMMS: null,
                    invoiceMethod: "",
                    paymentMode: ""
                  }
                : {
                    ...editableOrder,
                    invoiceMethod: editableOrder.invoiceMethod || "",
                    paymentMode: editableOrder.paymentMode || ""
                  }
            }
            validationSchema={Yup.object().shape({
              user: Yup.string().required(),
              totalAmount: Yup.number()
                .required()
                .min(1),
              currency: Yup.string().required(),
              centsPerSMS: Yup.number().nullable(),
              centsPerMMS: Yup.number().nullable(),
              invoiceMethod: Yup.string().required("Invoice Method is required"),
              paymentMode: Yup.string().required("Payment Mode is required"),
              ...(editableOrder && { usedAmount: Yup.number() })
            })}
            onSubmit={(values, { setSubmitting }) => {
              if (!editableOrder) {
                addPurchaseOrder(values)
                  .then(data => {
                    message.info("Saved");
                    onClose();
                  })
                  .finally(() => setSubmitting(false));
              } else {
                updatePurchaseOrder({
                  ...values,
                  user: user._id,
                  _id: editableOrder._id
                })
                  .then(() => {
                    message.info("Updated");
                    fetchOrders();
                    setEditableOrder(null);
                  })
                  .finally(() => setSubmitting(false));
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Row gutter={[20, 20]}>
                    <Col span={24}>
                      <Typography.Text strong className="fs-6">
                        Add Purchase Order
                      </Typography.Text>
                    </Col>
                    <Col span={12}>
                      <FormInput
                        label="Total Amount"
                        name={"totalAmount"}
                        value={values.totalAmount}
                        onChange={handleChange}
                        placeholder={"Enter Amount in cents"}
                        error={errors.totalAmount}
                        touched={touched.totalAmount}
                        type="number"
                        step="0.01"
                        containerClassname="mb-0"
                        size="default"
                        inputProps={{
                          ref: formikRef
                        }}
                      />
                    </Col>
                    {editableOrder && (
                      <Col span={12}>
                        <FormInput
                          label="Used Amount"
                          name={"usedAmount"}
                          value={values.usedAmount}
                          onChange={handleChange}
                          size="default"
                          placeholder={"Enter Amount in cents"}
                          error={errors.usedAmount}
                          touched={touched.usedAmount}
                          type="number"
                          step="0.01"
                          containerClassname="mb-0"
                        />
                      </Col>
                    )}
                    {currencyList && currencyList.length > 0 && (
                      <Col span={12}>
                        <FormSelectInput
                          label="Currency"
                          placeholder={"Select Currency"}
                          options={currencyList.map((currency, index) => ({
                            label: currency.country,
                            value: currency._id
                          }))}
                          name={"currency"}
                          onChange={value => {
                            setFieldValue("currency", value);
                          }}
                          value={values?.currency ? values.currency : undefined}
                          errors={errors.currency}
                          touched={touched.currency}
                          size="default"
                        />
                      </Col>
                    )}
                    <Col span={12}>
                      <FormInput
                        label="Cents Per SMS"
                        name={"centsPerSMS"}
                        value={values.centsPerSMS}
                        onChange={handleChange}
                        placeholder={"Enter Cents Per SMS"}
                        error={errors.centsPerSMS}
                        touched={touched.centsPerSMS}
                        type="number"
                        step="0.01"
                        size="default"
                        containerClassname="mb-0"
                      />
                    </Col>
                    <Col span={12}>
                      <FormInput
                        label="Cents Per MMS"
                        name={"centsPerMMS"}
                        value={values.centsPerMMS}
                        onChange={handleChange}
                        placeholder={"Enter Cents Per MMS"}
                        error={errors.centsPerMMS}
                        touched={touched.centsPerMMS}
                        type="number"
                        step="0.01"
                        size="default"
                        containerClassname="mb-0"
                      />
                    </Col>
                    <Col span={12}>
                      <FormSelectInput
                        label="Invoice Method"
                        placeholder="Select Invoice Method"
                        options={[
                          { label: "Usages", value: "usages" },
                          { label: "Credits", value: "credits" }
                        ]}
                        name="invoiceMethod"
                        onChange={value => setFieldValue("invoiceMethod", value)}
                        value={values.invoiceMethod || undefined}
                        errors={errors.invoiceMethod}
                        touched={touched.invoiceMethod}
                        size="default"
                      />
                    </Col>
                    <Col span={12}>
                      <FormSelectInput
                        label="Payment Mode"
                        placeholder="Select Payment Mode"
                        options={[
                          { label: "Reward", value: "reward" },
                          { label: "Stripe", value: "stripe" },
                          { label: "Invoice", value: "invoice" }
                        ]}
                        name="paymentMode"
                        onChange={value => setFieldValue("paymentMode", value)}
                        value={values.paymentMode || undefined}
                        errors={errors.paymentMode}
                        touched={touched.paymentMode}
                        size="default"
                      />
                    </Col>
                    <Col span={24}>
                      <FormCheckBoxInput
                        values={values}
                        handleChange={e => {
                          handleChange(e);
                          setFieldValue("isSMSAllow", e.target.checked);
                        }}
                        name={"isSMSAllow"}
                        placeholder={"is SMS Allowed ?"}
                        containerClassname="mb-0"
                      />
                    </Col>
                    <Col span={24}>
                      <FormCheckBoxInput
                        values={values}
                        handleChange={e => {
                          handleChange(e);
                          setFieldValue("isMMsAllow", e.target.checked);
                        }}
                        name={"isMMSAllow"}
                        placeholder={"is MMS Allowed ?"}
                        containerClassname="mb-0"
                      />
                    </Col>
                    <Col span={24}>
                      <AppButton type="submit" label={isSubmitting ? "Processing..." : !editableOrder ? "Add" : "Update"} light disabled={isSubmitting} />
                      {editableOrder && (
                        <AppButton
                          onClick={e => {
                            setEditableOrder(null);
                          }}
                          light
                          className="ms-2"
                          withoutBg
                          label={"Reset"}
                          disabled={isSubmitting}
                        />
                      )}
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </Col>
      <Divider />
      <Col span={24}>
        <Card className="rounded">
          <Row gutter={[20, 20]}>
            <Col span={24}>
              <Typography.Text strong className="fs-6">
                Previous Orders
              </Typography.Text>
            </Col>
            {orders && orders.length > 0 ? (
              orders.map(order => (
                <Col span={12} className="my-3" key={order._id}>
                  <Card
                    loading={deleting === order._id}
                    className="rounded"
                    hoverable
                    title={
                      <div className="d-flex align-items-center justify-content-between">
                        <Typography.Text>{moment(order.createdAt).format(dateTimeFormat)}</Typography.Text>
                        <Typography.Text>
                          <Tooltip placement="top" title="Delete">
                            <i
                              type="button"
                              className="text-danger-color fas fa-trash-alt fs-5"
                              onClick={() => {
                                setDeleting(order._id);
                                deleteOrder({ orderId: order._id })
                                  .then(() => message.info("Deleted"))
                                  .finally(() => setDeleting(null));
                              }}
                            />
                          </Tooltip>
                          <Tooltip placement="top" title="Edit">
                            <i
                              type="button"
                              className="text-info-color fas ms-2 fa-edit fs-5"
                              onClick={() => {
                                setEditableOrder(order);
                                formikRef.current.focus();
                              }}
                            />
                          </Tooltip>
                        </Typography.Text>
                      </div>
                    }
                  >
                    <Row gutter={20}>
                      <Col span={10}>
                        <Typography.Text strong>Total Amount</Typography.Text>
                      </Col>
                      <Col span={14}>
                        <Typography.Text strong className="text-info-color">
                          {order.totalAmount}
                        </Typography.Text>
                      </Col>
                    </Row>
                    <Row gutter={20}>
                      <Col span={10}>
                        <Typography.Text strong>Used Amount</Typography.Text>
                      </Col>
                      <Col span={14}>
                        <Typography.Text strong className="text-info-color">
                          {order.usedAmount}
                        </Typography.Text>
                      </Col>
                    </Row>
                    {order.isSMSAllow && (
                      <Row gutter={20}>
                        <Col span={10}>
                          <Typography.Text strong>Cents Per SMS</Typography.Text>
                        </Col>
                        <Col span={14}>
                          <Typography.Text strong className="text-info-color">
                            {order.centsPerSMS}
                          </Typography.Text>
                        </Col>
                      </Row>
                    )}
                    {order.isMMSAllow && (
                      <Row gutter={20}>
                        <Col span={10}>
                          <Typography.Text strong>Cents Per MMS</Typography.Text>
                        </Col>
                        <Col span={14}>
                          <Typography.Text strong className="text-info-color">
                            {order.centsPerMMS}
                          </Typography.Text>
                        </Col>
                      </Row>
                    )}
                    <Row gutter={20}>
                      <Col span={10}>
                        <Typography.Text strong>Medium</Typography.Text>
                      </Col>
                      <Col span={14}>
                        <Typography.Text strong className="text-info-color">
                          {order.type}
                        </Typography.Text>
                      </Col>
                    </Row>
                    {order?.paymentMode && (
                      <Row gutter={20}>
                        <Col span={10}>
                          <Typography.Text strong>Payment Mode</Typography.Text>
                        </Col>
                        <Col span={14}>
                          <Typography.Text strong className="text-info-color">
                            {order.paymentMode}
                          </Typography.Text>
                        </Col>
                      </Row>
                    )}
                    {order?.invoiceMethod && (
                      <Row gutter={20}>
                        <Col span={10}>
                          <Typography.Text strong>Invoice Method</Typography.Text>
                        </Col>
                        <Col span={14}>
                          <Typography.Text strong className="text-info-color">
                            {order.invoiceMethod}
                          </Typography.Text>
                        </Col>
                      </Row>
                    )}
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Previous Orders" />
              </Col>
            )}
          </Row>
        </Card>
      </Col>
    </Row>
  );
}

export default inject(stores => ({
  userStore: stores.store.userStore
}))(observer(PurchaseOrder));
